import { RudderAnalytics } from '@rudderstack/analytics-js'
import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'
import { getOperatingSystemVersion } from 'helpers/getOperatingSystemVersion'
import type {
  IEventLogger,
  IEventLogParams,
  TCommonEventProperties,
} from 'models/events.model'
import { CurrentEnvironment, EventLoggerInstanceName } from 'root-constants'

export class Rudderstack implements IEventLogger {
  public readonly name: EventLoggerInstanceName
  private readonly rudderstack: RudderAnalytics
  private userId: string
  private utmTags: Record<string, string>
  private cohort = ''
  private userProperties: Record<string, unknown> = {}

  constructor({
    instanceName,
    writeKey,
    dataPlaneUrl,
    userId,
    utmTags,
    cohort,
    options,
  }: {
    instanceName: EventLoggerInstanceName
    writeKey: string
    dataPlaneUrl: string
    userId: string
    utmTags: Record<string, string>
    cohort: string
    options?: Record<string, unknown>
  }) {
    this.cohort = cohort
    this.utmTags = utmTags
    this.name = instanceName
    this.userId = userId
    this.rudderstack = new RudderAnalytics()
    this.rudderstack.load(writeKey, dataPlaneUrl, {
      consentManagement: { enabled: false },
      configUrl: dataPlaneUrl,
      onLoaded: () => {
        this.setUserProperties(options)
      },
    })
  }

  log({ event, eventProperty, isPageEvent, cb }: IEventLogParams): void {
    const mergedEventProperty: TCommonEventProperties = {
      cohort: this.cohort,
      attribution: this.utmTags,
      operating_system_version: getOperatingSystemVersion(),
      is_prod_env: import.meta.env.CURRENT_ENV === CurrentEnvironment.PROD,
      ...eventProperty,
    }

    if (isPageEvent) {
      // https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#page
      // Page call with page name, properties, and callback
      // rudderanalytics.page(name, [properties], [callback]);
      this.rudderstack.page(
        eventProperty?.page_name || 'unknown',
        mergedEventProperty,
        cb,
      )
    }

    this.rudderstack.track(event, mergedEventProperty, cb)
  }

  updateUserProperties(customProps: Record<string, any>): void {
    const updatedUserProperties = {
      ...this.userProperties,
      ...customProps,
    }

    this.userProperties = updatedUserProperties
    this.setUserProperties(updatedUserProperties)
  }

  private setUserProperties(options?: Record<string, unknown>): void {
    const userProperties = {
      'Device Type': getMobileOperatingSystem(),
      'Operating System Version': getOperatingSystemVersion(),
      env: import.meta.env.CURRENT_ENV,
      attribution: this.utmTags,
      ...options,
    }

    this.userProperties = userProperties
    this.rudderstack.identify(this.userId, userProperties)
  }
}
